import {
    AIRCRAFT_LABEL_GAP_PX,
    AIRCRAFT_LABEL_HEIGHT_PX,
    AIRCRAFT_LABEL_NUDGE_STEP_PX,
    AIRCRAFT_LABEL_OFFSET_Y_PX,
    AIRCRAFT_LABEL_WIDTH_PX,
    CROSSING_HIT_RADIUS_PX,
    LABEL_OVERLAP_PAD_PX,
    MIN_VIEW_SPAN_NM,
    QDM_LABEL_CHAR_WIDTH_PX,
    QDM_LABEL_GAP_PX,
    QDM_LABEL_HEIGHT_PX,
    VIEW_MARGIN_NM,
    VIEW_PADDING_FACTOR,
} from './constants.js';
import { dom } from './dom.js';
import { state } from './state.js';
import { radToDeg } from './utils.js';

function worldToScreen(worldX, worldY) {
    const screenX = dom.canvas.width / 2 + (worldX - state.viewCenter.x) * state.scale;
    const screenY = dom.canvas.height / 2 + (worldY - state.viewCenter.y) * state.scale;
    return { x: screenX, y: screenY };
}

export function hitTestCrossing(screenX, screenY) {
    if (!state.intersectionPoint) return false;
    const { x, y } = worldToScreen(state.intersectionPoint.x, state.intersectionPoint.y);
    const dx = screenX - x;
    const dy = screenY - y;
    return dx * dx + dy * dy <= CROSSING_HIT_RADIUS_PX * CROSSING_HIT_RADIUS_PX;
}

/** Place label opposite the track direction toward the crossing. */
function getAircraftLabelSide(ac, intersection) {
    const dx = intersection.x - ac.x;
    const dy = intersection.y - ac.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
        return dx > 0 ? 'left' : 'right';
    }
    return dy > 0 ? 'left' : 'right';
}

function getQdmLabelSide(ac, intersection) {
    return getAircraftLabelSide(ac, intersection) === 'left' ? 'right' : 'left';
}

function estimateQdmLabelWidth(text) {
    return Math.max(72, text.length * QDM_LABEL_CHAR_WIDTH_PX);
}

function padRect(rect, pad = LABEL_OVERLAP_PAD_PX) {
    return {
        left: rect.left - pad,
        top: rect.top - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
    };
}

function rectsOverlap(a, b) {
    return (
        a.left < b.left + b.width &&
        a.left + a.width > b.left &&
        a.top < b.top + b.height &&
        a.top + a.height > b.top
    );
}

function getAircraftLabelRect(screenX, screenY, side, offsetY = 0) {
    const top = screenY + AIRCRAFT_LABEL_OFFSET_Y_PX + offsetY;
    if (side === 'left') {
        return {
            left: screenX - AIRCRAFT_LABEL_GAP_PX - AIRCRAFT_LABEL_WIDTH_PX,
            top,
            width: AIRCRAFT_LABEL_WIDTH_PX,
            height: AIRCRAFT_LABEL_HEIGHT_PX,
        };
    }
    return {
        left: screenX + AIRCRAFT_LABEL_GAP_PX,
        top,
        width: AIRCRAFT_LABEL_WIDTH_PX,
        height: AIRCRAFT_LABEL_HEIGHT_PX,
    };
}

function getQdmLabelRect(screenX, screenY, side, text) {
    const width = estimateQdmLabelWidth(text);
    const height = QDM_LABEL_HEIGHT_PX;
    const top = screenY - height / 2;
    if (side === 'right') {
        return { left: screenX + QDM_LABEL_GAP_PX, top, width, height };
    }
    return { left: screenX - QDM_LABEL_GAP_PX - width, top, width, height };
}

function buildQdmLabelText(ac, otherAc, intersection) {
    const dx = intersection.x - ac.x;
    const dy = intersection.y - ac.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const time = distance / (ac.speed / 60);
    const radial = (radToDeg(Math.atan2(dx, -dy)) + 360) % 360;
    const otherDx = intersection.x - otherAc.x;
    const otherDy = intersection.y - otherAc.y;
    const otherDistance = Math.sqrt(otherDx * otherDx + otherDy * otherDy);
    const relativeDistance = distance - otherDistance;
    const formattedDist = distance >= 10 ? Math.round(distance) : distance.toFixed(1);
    const formattedTime = Math.round(time);
    const bothConnected =
        state.qdmConnected.aircraft1 && state.qdmConnected.aircraft2;
    const relativePart = bothConnected
        ? ` (${relativeDistance > 0 ? '+' : ''}${Math.round(relativeDistance)})`
        : '';
    return `${Math.round(radial)}° / ${formattedDist}${relativePart} / ${formattedTime}'`;
}

function findClearAircraftLabelOffset(layout, obstacles) {
    const step = AIRCRAFT_LABEL_NUDGE_STEP_PX;
    const candidates = [step, step * 1.5, step * 2, -step, -step * 1.5, 0];
    for (const offsetY of candidates) {
        const rect = padRect(
            getAircraftLabelRect(layout.screenX, layout.screenY, layout.side, offsetY),
        );
        if (!obstacles.some((obstacle) => rectsOverlap(rect, padRect(obstacle)))) {
            return offsetY;
        }
    }
    return step * 2;
}

function resolveAircraftLabelLayouts() {
    const intersection = state.intersectionPoint;
    const entries = [
        { key: 'aircraft1', ac: state.aircraft1 },
        { key: 'aircraft2', ac: state.aircraft2 },
    ];
    const layouts = {};

    entries.forEach(({ key, ac }) => {
        const { x: screenX, y: screenY } = worldToScreen(ac.x, ac.y);
        layouts[key] = {
            screenX,
            screenY,
            side: getAircraftLabelSide(ac, intersection),
            offsetY: 0,
        };
    });

    const qdmRects = entries
        .filter(({ key }) => state.qdmConnected[key])
        .map(({ key, ac }) => {
            const otherAc = key === 'aircraft1' ? state.aircraft2 : state.aircraft1;
            const { x: screenX, y: screenY } = worldToScreen(ac.x, ac.y);
            const text = buildQdmLabelText(ac, otherAc, intersection);
            return {
                source: key,
                rect: getQdmLabelRect(
                    screenX,
                    screenY,
                    getQdmLabelSide(ac, intersection),
                    text,
                ),
            };
        });

    entries.forEach(({ key }) => {
        const layout = layouts[key];
        const obstacles = qdmRects
            .filter((qdm) => qdm.source !== key)
            .map((qdm) => qdm.rect);
        if (obstacles.length === 0) return;

        const baseRect = padRect(
            getAircraftLabelRect(layout.screenX, layout.screenY, layout.side, layout.offsetY),
        );
        if (!obstacles.some((obstacle) => rectsOverlap(baseRect, padRect(obstacle)))) {
            return;
        }
        layout.offsetY = findClearAircraftLabelOffset(layout, obstacles);
    });

    return layouts;
}

function getAircraftLabelWorldBounds(ac, intersection, scale, offsetY = 0) {
    const side = getAircraftLabelSide(ac, intersection);
    const gapNm = AIRCRAFT_LABEL_GAP_PX / scale;
    const widthNm = AIRCRAFT_LABEL_WIDTH_PX / scale;
    const heightNm = AIRCRAFT_LABEL_HEIGHT_PX / scale;
    const offsetYNm = (AIRCRAFT_LABEL_OFFSET_Y_PX + offsetY) / scale;

    if (side === 'left') {
        return {
            minX: ac.x - gapNm - widthNm,
            maxX: ac.x - gapNm,
            minY: ac.y + offsetYNm,
            maxY: ac.y + offsetYNm + heightNm,
        };
    }
    return {
        minX: ac.x + gapNm,
        maxX: ac.x + gapNm + widthNm,
        minY: ac.y + offsetYNm,
        maxY: ac.y + offsetYNm + heightNm,
    };
}

function drawCrossingMarker() {
    const { x, y } = worldToScreen(state.intersectionPoint.x, state.intersectionPoint.y);
    dom.ctx.beginPath();
    dom.ctx.arc(x, y, 6, 0, 2 * Math.PI);
    dom.ctx.fillStyle = '#fbbf24';
    dom.ctx.fill();
    dom.ctx.strokeStyle = '#ffffff';
    dom.ctx.lineWidth = 2;
    dom.ctx.stroke();
}

function drawAircraft(ac, aircraftKey, color, layout) {
    const { x: screenX, y: screenY } = worldToScreen(ac.x, ac.y);
    const AIRCRAFT_RADIUS = 5;
    dom.ctx.beginPath();
    dom.ctx.arc(screenX, screenY, AIRCRAFT_RADIUS, 0, 2 * Math.PI);
    dom.ctx.fillStyle = color;
    dom.ctx.fill();
    dom.ctx.strokeStyle = 'white';
    dom.ctx.lineWidth = 1;
    dom.ctx.stroke();

    const labelText = `${ac.callsign}\n${ac.fl}  N${ac.speed}`;
    const labelDiv = document.createElement('div');
    labelDiv.className = 'aircraft-label absolute text-white';
    labelDiv.dataset.aircraft = aircraftKey;
    labelDiv.style.top = `${screenY + AIRCRAFT_LABEL_OFFSET_Y_PX + layout.offsetY}px`;
    if (layout.side === 'left') {
        labelDiv.style.left = `${screenX - AIRCRAFT_LABEL_GAP_PX}px`;
        labelDiv.style.transform = 'translate(-100%, 0)';
    } else {
        labelDiv.style.left = `${screenX + AIRCRAFT_LABEL_GAP_PX}px`;
        labelDiv.style.transform = '';
    }
    labelDiv.innerText = labelText;

    if (state.crossingMarked && !state.qdmConnected[aircraftKey]) {
        labelDiv.classList.add('aircraft-label--selectable');
    }

    dom.radarContainer.appendChild(labelDiv);
}

function getRouteLineScreenLength() {
    const { width, height } = dom.canvas;
    // Cover full canvas plus max pan offset (half screen each way) in every direction.
    return Math.hypot(width + width, height + height);
}

function drawRoutes(ac) {
    const { x: screenX, y: screenY } = worldToScreen(ac.x, ac.y);
    const length = getRouteLineScreenLength();
    dom.ctx.beginPath();
    dom.ctx.moveTo(screenX, screenY);
    dom.ctx.lineTo(
        screenX + length * Math.sin(ac.headingRad),
        screenY - length * Math.cos(ac.headingRad),
    );
    dom.ctx.strokeStyle = '#7CFC00';
    dom.ctx.lineWidth = 0.5;
    dom.ctx.stroke();
}

function drawQdmLine(ac, intersection) {
    const { x: acScreenX, y: acScreenY } = worldToScreen(ac.x, ac.y);
    const { x: intScreenX, y: intScreenY } = worldToScreen(intersection.x, intersection.y);
    dom.ctx.beginPath();
    dom.ctx.moveTo(acScreenX, acScreenY);
    dom.ctx.lineTo(intScreenX, intScreenY);
    dom.ctx.strokeStyle = '#D97706';
    dom.ctx.lineWidth = 2;
    dom.ctx.stroke();
}

function appendQdmLabel(ac, otherAc, intersection) {
    const { x: acScreenX, y: acScreenY } = worldToScreen(ac.x, ac.y);
    const labelText = buildQdmLabelText(ac, otherAc, intersection);
    const qdmSide = getQdmLabelSide(ac, intersection);

    const labelDiv = document.createElement('div');
    labelDiv.className = 'qdm-label';
    labelDiv.style.top = `${acScreenY}px`;
    if (qdmSide === 'right') {
        labelDiv.style.left = `${acScreenX + QDM_LABEL_GAP_PX}px`;
        labelDiv.style.transform = 'translate(0, -50%)';
    } else {
        labelDiv.style.left = `${acScreenX - QDM_LABEL_GAP_PX}px`;
        labelDiv.style.transform = 'translate(-100%, -50%)';
    }
    labelDiv.innerText = labelText;
    dom.radarContainer.appendChild(labelDiv);
}

function clampViewOffset() {
    const { width, height } = dom.radarContainer.getBoundingClientRect();
    const maxOffsetX = width / 2;
    const maxOffsetY = height / 2;
    state.viewOffset.x = Math.max(-maxOffsetX, Math.min(maxOffsetX, state.viewOffset.x));
    state.viewOffset.y = Math.max(-maxOffsetY, Math.min(maxOffsetY, state.viewOffset.y));
}

function applyViewCenterFromOffset() {
    clampViewOffset();
    state.viewCenter.x = state.baseViewCenter.x - state.viewOffset.x / state.scale;
    state.viewCenter.y = state.baseViewCenter.y - state.viewOffset.y / state.scale;
}

export function resetViewOffset() {
    state.viewOffset.x = 0;
    state.viewOffset.y = 0;
}

export function panView(deltaScreenX, deltaScreenY) {
    if (!state.scale) return;
    state.viewOffset.x += deltaScreenX;
    state.viewOffset.y += deltaScreenY;
    applyViewCenterFromOffset();
    redrawRadar();
}

function redrawRadar() {
    const { width, height } = dom.radarContainer.getBoundingClientRect();
    dom.canvas.width = width;
    dom.canvas.height = height;
    dom.ctx.fillStyle = getComputedStyle(dom.radarContainer).backgroundColor;
    dom.ctx.fillRect(0, 0, width, height);
    drawScenario();
}

function drawScenario() {
    dom.radarContainer.querySelectorAll('.aircraft-label, .qdm-label').forEach((el) => el.remove());
    drawRoutes(state.aircraft1);
    drawRoutes(state.aircraft2);

    if (state.crossingMarked) {
        drawCrossingMarker();
    }

    const labelLayouts = resolveAircraftLabelLayouts();

    if (state.qdmConnected.aircraft1) {
        drawQdmLine(state.aircraft1, state.intersectionPoint);
        appendQdmLabel(state.aircraft1, state.aircraft2, state.intersectionPoint);
    }
    if (state.qdmConnected.aircraft2) {
        drawQdmLine(state.aircraft2, state.intersectionPoint);
        appendQdmLabel(state.aircraft2, state.aircraft1, state.intersectionPoint);
    }

    drawAircraft(state.aircraft1, 'aircraft1', '#3b82f6', labelLayouts.aircraft1);
    drawAircraft(state.aircraft2, 'aircraft2', '#ef4444', labelLayouts.aircraft2);
}

export function updateViewAndDraw() {
    if (!state.aircraft1 || !state.aircraft2) return;

    const { width, height } = dom.radarContainer.getBoundingClientRect();
    dom.canvas.width = width;
    dom.canvas.height = height;
    dom.ctx.fillStyle = getComputedStyle(dom.radarContainer).backgroundColor;
    dom.ctx.fillRect(0, 0, width, height);

    const basePoints = [
        { x: state.aircraft1.x, y: state.aircraft1.y },
        { x: state.aircraft2.x, y: state.aircraft2.y },
        { x: state.intersectionPoint.x, y: state.intersectionPoint.y },
    ];
    const baseMinX = Math.min(...basePoints.map((p) => p.x));
    const baseMaxX = Math.max(...basePoints.map((p) => p.x));
    const baseMinY = Math.min(...basePoints.map((p) => p.y));
    const baseMaxY = Math.max(...basePoints.map((p) => p.y));
    const baseSpanX = Math.max(baseMaxX - baseMinX, MIN_VIEW_SPAN_NM) + VIEW_MARGIN_NM * 2;
    const baseSpanY = Math.max(baseMaxY - baseMinY, MIN_VIEW_SPAN_NM) + VIEW_MARGIN_NM * 2;
    const initialScale = Math.min(
        width / (baseSpanX * VIEW_PADDING_FACTOR),
        height / (baseSpanY * VIEW_PADDING_FACTOR),
    );

    const maxLabelNudge = AIRCRAFT_LABEL_NUDGE_STEP_PX * 2;
    const points = [...basePoints];
    [state.aircraft1, state.aircraft2].forEach((ac) => {
        const bounds = getAircraftLabelWorldBounds(
            ac,
            state.intersectionPoint,
            initialScale,
            maxLabelNudge,
        );
        points.push(
            { x: bounds.minX, y: bounds.minY },
            { x: bounds.maxX, y: bounds.minY },
            { x: bounds.minX, y: bounds.maxY },
            { x: bounds.maxX, y: bounds.maxY },
        );
    });

    const minX = Math.min(...points.map((p) => p.x));
    const maxX = Math.max(...points.map((p) => p.x));
    const minY = Math.min(...points.map((p) => p.y));
    const maxY = Math.max(...points.map((p) => p.y));
    const spanX = Math.max(maxX - minX, MIN_VIEW_SPAN_NM) + VIEW_MARGIN_NM * 2;
    const spanY = Math.max(maxY - minY, MIN_VIEW_SPAN_NM) + VIEW_MARGIN_NM * 2;
    state.scale = Math.min(
        width / (spanX * VIEW_PADDING_FACTOR),
        height / (spanY * VIEW_PADDING_FACTOR),
    );
    state.baseViewCenter.x = (minX + maxX) / 2;
    state.baseViewCenter.y = (minY + maxY) / 2;
    applyViewCenterFromOffset();
    redrawRadar();
}
